// Benchmark position card. Two stacked, axis-aligned views over the 0-100
// score scale: a peer-distribution graph on top (the field), and a position
// bar beneath it carrying the org's marker plus distinct, colour-coded
// reference points (platform, industry when licensed, and the top-quartile
// goal line). The reader sees where they fall in the spread and draws their
// own conclusion; the card does not assert a verdict.
//
// What changed, and why:
//   - REMOVED the hero "safer than X%" sentence: too on-the-nose. The
//     distribution + bar let the reader reach the conclusion themselves.
//   - ADDED a clear distribution graph above the bar, sharing the bar's
//     exact horizontal scale so the You guide line threads from the curve
//     straight down to the marker. The distribution is MODELLED from the
//     cohort average (we have no real per-peer data yet) and labelled as
//     such, so it is honest about being a shape, not measured spread.
//   - KEPT platform and industry as two separate, clearly distinguished
//     points (grey / teal, matching the trend chart) with their own values;
//     any labels that would collide auto-stagger onto a lower row.
//   - Hover/focus detail for any band or reference point lands in a fixed
//     panel at the foot of the card, not a floating tooltip, so it sits in
//     the card's own visual language and stays put while the eye scans.
//
// Postgres mapping: `industry_benchmarks` (industry_id, metric_id, value)
// refreshed monthly; the real per-peer distribution would come from the same
// service and replace the modelled curve. Integration hook:
// `useBenchmarkQuery({ industry })` cache 24h.
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { motion } from 'motion/react';
import MainCard from '../../../../components/cards/MainCard';
import { ORG_SCORE, BENCHMARK, BANDS, bandFor } from '../../riskScore';
import useFeature from '../../useFeature';
import { peerCount } from './benchmarkPercentile';
import useDashboardData from '../../hooks/useDashboardData';

const MIN = 0;
const MAX = 100;
const pos = (score) => ((score - MIN) / (MAX - MIN)) * 100;

// Reference-point colours, shared with RiskTrendChart so platform/industry
// read as the same series across the dashboard.
const TOP25_COLOR = '#66BB6A';
const PLATFORM_COLOR = '#6B7591';
const INDUSTRY_COLOR = '#00BFA5';

// Minimum horizontal gap (% of rail) before two reference labels are
// considered colliding and the later one is bumped to a lower row.
// 13 (was 11): the single-line "● LABEL · value" block is wider than the old
// stacked two-line one, so near-misses need to stagger a little sooner.
const LABEL_MIN_GAP = 13;
const ROW_STEP = 21; // px between stacked label rows

// Modelled distribution geometry (viewBox units == score, so x = score).
const DIST_VB_H = 40;
const DIST_SD = 15;

export default function BenchmarkPositionCard({ orgScore, scope }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const industryOn = useFeature('industryBenchmarking');
    const benchQ = useDashboardData({ metric: 'simulations', aggregation: 'risk_benchmark', scope });
    const bench = benchQ.data?.riskBenchmark || null;

    // null when no score exists - the card then renders a no-data note instead
    // of asserting an invented mid-scale position.
    const score = (typeof orgScore === 'number' ? orgScore : ORG_SCORE) ?? BENCHMARK.you ?? null;
    const band = bandFor(score) || BENCHMARK.youBand;
    const youColor = band?.color || theme.palette.primary.main;

    // Hover-overs surface in a fixed panel at the foot of the card rather than
    // floating tooltips, so the detail sits in the card's own visual language
    // and stays put while the reader scans the bar. Defaults to the org's own
    // score when nothing is hovered, so the panel is never empty.
    const defaultInfo = {
        label: 'You',
        value: score,
        color: youColor,
        tip: `Your organisation's current risk score${band?.name ? `, in the "${band.name}" band` : ''}. Lower is better. Hover a band or reference point for detail.`,
    };
    const [info, setInfo] = React.useState(null);
    const active = info || defaultInfo;

    if (score == null) {
        return (
            <MainCard content={false} sx={{ bgcolor: 'background.paper', height: '100%' }} border={false} boxShadow={true}>
                <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>No benchmark position yet</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                        Your position against peers appears once the organisation has a risk score.
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    const top25 = bench?.top25 ?? BENCHMARK.top25;
    const platformAvg = bench?.platformAvg ?? BENCHMARK.platformAvg;
    // Real per-industry cohort average from the server. Null when this org has no industry set, the
    // cohort is below the anonymity floor, or the feed hasn't loaded - in every one of those cases the
    // Industry reference point is simply omitted (never the old mock, which would assert a fake cohort).
    const industryAvg = bench?.industryAvg ?? null;
    const industryPeers = bench?.industryPeers ?? 0;
    const platformPeers = bench?.peers ?? 0;

    // Reference points as distinct, colour-coded marks, auto-staggered so
    // close values (platform/industry) keep legible labels.
    // Reference points only render when the platform actually has a value
    // for them (industry has no data source today; platform anchors need
    // the cross-tenancy benchmark feed).
    const refs = [
        {
            key: 'top25', label: 'Top 25%', value: top25, color: TOP25_COLOR, dashed: true,
            tip: `Top 25% of organisations on the platform by risk score. A score of ${top25} or lower puts you in the safest quarter.`,
        },
        {
            key: 'platform', label: 'Platform', value: platformAvg, color: PLATFORM_COLOR,
            tip: platformPeers > 0
                ? `Average risk score across all ${platformPeers} organisations on the platform.`
                : `Average risk score across all organisations on the platform.`,
        },
        ...(industryOn ? [{
            key: 'industry', label: 'Industry', value: industryAvg, color: INDUSTRY_COLOR,
            tip: industryPeers > 0
                ? `Average risk score across the ${industryPeers} organisations in your industry.`
                : `Average risk score across organisations in your industry.`,
        }] : []),
    ].filter((r) => r.value != null).sort((a, b) => a.value - b.value);

    const placed = [];
    refs.forEach((r) => {
        const p = pos(r.value);
        let row = 0;
        while (placed.some((q) => q.row === row && Math.abs(q.p - p) < LABEL_MIN_GAP)) row += 1;
        placed.push({ ...r, p, row });
    });
    const maxRow = placed.reduce((m, r) => Math.max(m, r.row), 0);

    // Pin the You pill's edge, not its centre, to the tick when a nearby
    // reference point would otherwise be spilled over.
    const youP = pos(score);
    const crowdedBelow = refs.some((r) => youP - pos(r.value) > 0 && youP - pos(r.value) < LABEL_MIN_GAP);
    const crowdedAbove = refs.some((r) => pos(r.value) - youP > 0 && pos(r.value) - youP < LABEL_MIN_GAP);
    const youAnchor = crowdedBelow && !crowdedAbove ? 'start' : crowdedAbove && !crowdedBelow ? 'end' : 'center';

    // Modelled peer distribution: a bell over the platform-wide field (all
    // orgs), centred on the platform average. The field is the same whether
    // or not the industry feature is licensed; industry is a reference point
    // within it, not a different population. Pure shape, hence the "modelled"
    // label; swap for the real per-peer distribution when the benchmarking
    // service exposes it.
    // Only draw the modelled peer distribution when there's a real platform
    // average to centre it on; with no benchmark data the curve would depict
    // a peer field that doesn't exist, so it simply doesn't render (matching
    // the reference-marker non-render below).
    const hasPlatform = platformAvg != null;
    const { areaPath, linePath } = hasPlatform ? buildDistribution(platformAvg) : { areaPath: null, linePath: null };
    // Count what we average: the platformAvg comes from risk_benchmark's cohort
    // (every scored tenancy), so the subtitle/tip must quote THAT peer count. The
    // legacy peerCount() (volume-floored benchmark_snapshot, min 20 sends) is a
    // different, smaller cohort and read "4 platform orgs" under an average of 14.
    const cohort = platformPeers > 0 ? platformPeers : peerCount();

    // The card's job, stated plainly and feature-aware: how you compare to
    // the industry (when licensed) and to the platform. Drop the cohort count
    // when it's unknown rather than rendering "0 platform orgs".
    const platformPhrase = cohort > 0 ? `${cohort} platform orgs` : 'the platform';
    const subtitle = industryOn
        ? `You vs your industry vs ${platformPhrase} · lower is better`
        : `You vs ${platformPhrase} · lower is better`;

    return (
        <MainCard
            content={false}
            border={false}
            boxShadow
            sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', ...(isDark && { boxShadow: `0 0 0 1px rgba(255,255,255,0.06), 0 4px 24px rgba(0,0,0,0.4)` }) }}
        >
            <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', gap: 2, overflow: 'visible' }}>
                {/* Header */}
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                        Benchmark position
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{subtitle}</Typography>
                </Box>

                {/* Distribution + bar share the same horizontal scale (0-100),
                    so the You guide threads from the curve down to its marker. */}
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', px: { xs: 2, sm: 1 } }}>

                    {/* Distribution graph */}
                    <Box sx={{ position: 'relative' }}>
                        {hasPlatform && (
                            <Typography sx={{ position: 'absolute', top: 0, right: 0, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', zIndex: 1 }}>
                                Modelled curve · real averages
                            </Typography>
                        )}
                        <Box
                            component="svg"
                            viewBox={`0 0 100 ${DIST_VB_H}`}
                            preserveAspectRatio="none"
                            role="img"
                            aria-label={hasPlatform
                                ? `Modelled distribution of platform peer risk scores, centred near ${platformAvg}. Your organisation scores ${score}; platform average ${platformAvg}${industryOn && industryAvg != null ? `, industry average ${industryAvg}` : ''}.`
                                : `Your organisation scores ${score}. No peer benchmark data available.`}
                            sx={{ width: '100%', height: 54, display: 'block', overflow: 'visible' }}
                        >
                            {hasPlatform && (
                                <>
                                    <motion.path
                                        d={areaPath}
                                        fill={alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08)}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.5, delay: 0.2 }}
                                    />
                                    <motion.path
                                        d={linePath}
                                        fill="none"
                                        stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.32)'}
                                        strokeWidth={1.25}
                                        vectorEffect="non-scaling-stroke"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.8, ease: 'easeOut' }}
                                    />
                                </>
                            )}
                            {/* You guide line, aligned to the marker on the bar below */}
                            <line x1={score} x2={score} y1={2} y2={DIST_VB_H} stroke={youColor} strokeWidth={2} vectorEffect="non-scaling-stroke" />
                        </Box>
                    </Box>

                    {/* Position bar */}
                    <Box sx={{ position: 'relative', mt: '34px', mb: `${28 + maxRow * ROW_STEP}px` }}>
                        {/* Band-tinted track: green (low) left, red (critical)
                            right, so colour signals "left is better". */}
                        <Box sx={{ position: 'relative', height: 12, borderRadius: '999px', overflow: 'hidden', display: 'flex', border: '1px solid', borderColor: 'divider' }}>
                            {BANDS.map((b) => {
                                const width = ((b.max - b.min + 1) / (MAX - MIN + 1)) * 100;
                                const bandInfo = {
                                    label: b.name,
                                    value: `${b.min}-${b.max}`,
                                    color: b.color,
                                    tip: `Risk band "${b.name}": scores from ${b.min} to ${b.max}.`,
                                };
                                return (
                                    <Box
                                        key={b.name}
                                        component={motion.div}
                                        tabIndex={0}
                                        role="button"
                                        aria-label={`${b.name} band, scores ${b.min} to ${b.max}`}
                                        onMouseEnter={() => setInfo(bandInfo)}
                                        onMouseLeave={() => setInfo(null)}
                                        onFocus={() => setInfo(bandInfo)}
                                        onBlur={() => setInfo(null)}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: isDark ? 0.32 : 0.22 }}
                                        whileHover={{ opacity: isDark ? 0.65 : 0.5 }}
                                        transition={{ duration: 0.18, ease: 'easeOut' }}
                                        sx={{
                                            width: `${width}%`,
                                            height: '100%',
                                            bgcolor: b.color,
                                            cursor: 'pointer',
                                            // box-shadow (the colour-framed highlight) animates via
                                            // CSS; framer drives the opacity brighten. Only these two
                                            // properties transition, never `all`.
                                            transition: 'box-shadow 180ms ease',
                                            '&:hover': { boxShadow: `inset 0 2px 0 ${b.color}, inset 0 -2px 0 ${b.color}` },
                                            '&:focus-visible': { outline: `2px solid ${b.color}`, outlineOffset: 2 },
                                        }}
                                    />
                                );
                            })}
                        </Box>

                        {/* Reference points: platform / industry / top 25% */}
                        {placed.map((r, i) => (
                            <RefMarker
                                key={r.key}
                                {...r}
                                isDark={isDark}
                                delay={0.5 + i * 0.08}
                                onShow={() => setInfo({ label: r.label, value: r.value, color: r.color, tip: r.tip })}
                                onHide={() => setInfo(null)}
                            />
                        ))}

                        {/* You */}
                        <YouMarker left={youP} value={score} color={youColor} isDark={isDark} anchor={youAnchor} />
                    </Box>

                    {/* Directional axis labels (replace bare 0 / 100) */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.25 }}>
                        <Typography sx={{ fontSize: 9, fontWeight: 600, color: 'text.disabled' }}>Lower risk</Typography>
                        <Typography sx={{ fontSize: 9, fontWeight: 600, color: 'text.disabled' }}>Higher risk</Typography>
                    </Box>
                </Box>

                {/* Detail window: hover-over text for the hovered band or
                    reference point lands here instead of in a floating tooltip. */}
                <Box
                    aria-live="polite"
                    sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', gap: 1, height: 62, flexShrink: 0 }}
                >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: active.color, mt: '3px', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', lineHeight: 1.4 }}>
                            {active.label}{active.value != null ? ` · ${active.value}` : ''}
                        </Typography>
                        {/* Reserve a fixed two-line area so swapping messages of
                            different length never resizes the card. */}
                        <Typography
                            sx={{
                                fontSize: 11, color: 'text.secondary', lineHeight: 1.45,
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {active.tip}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </MainCard>
    );
}

// Build the modelled distribution as SVG path strings in 0-100 x units (so
// x == score) over a fixed viewBox height.
function buildDistribution(mu) {
    const baseY = DIST_VB_H - 2;
    const topY = 3;
    const dens = (s) => Math.exp(-((s - mu) * (s - mu)) / (2 * DIST_SD * DIST_SD));
    let line = '';
    let area = `M 0 ${baseY}`;
    for (let s = 0; s <= 100; s += 1) {
        const y = (baseY - dens(s) * (baseY - topY)).toFixed(2);
        line += `${s === 0 ? 'M' : 'L'} ${s} ${y} `;
        area += ` L ${s} ${y}`;
    }
    area += ` L 100 ${baseY} Z`;
    return { areaPath: area, linePath: line.trim() };
}

// anchor 'start'/'end' pins the pill's near edge to the tick instead of
// centring it, so it only extends away from a crowded neighbour.
// Positioning transforms live on plain wrapper divs, not the motion.div:
// Framer owns `transform` on anything it animates and drops any translateX
// that isn't its own, which was putting every marker at the wrong x.
function YouMarker({ left, value, color, isDark, anchor = 'center' }) {
    const pillShiftX = anchor === 'start' ? '0%' : anchor === 'end' ? '-100%' : '-50%';
    return (
        <Box sx={{ position: 'absolute', left: `${left}%`, top: '50%', width: 0, height: 22, transform: 'translateY(-50%)', zIndex: 3 }}>
            <Box sx={{ position: 'absolute', left: 0, bottom: 'calc(100% + 8px)', transform: `translateX(${pillShiftX})` }}>
                <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.7 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.45, ease: [0.34, 1.4, 0.64, 1] }}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '3px 8px', borderRadius: 6, background: color,
                        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)' : '0 4px 12px rgba(0,0,0,0.18)',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fff', opacity: 0.85 }}>You</span>
                    <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '-0.02em', color: '#fff' }}>{value}</span>
                </motion.div>
            </Box>
            <Box sx={{ position: 'absolute', left: 0, bottom: 'calc(100% + 2px)', transform: 'translateX(-50%)' }}>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.6 }}
                    style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `6px solid ${color}` }}
                />
            </Box>
            <Box sx={{ position: 'absolute', left: 0, top: 0, transform: 'translateX(-50%)' }}>
                <motion.div
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{ duration: 0.4, delay: 0.4, ease: 'easeOut' }}
                    style={{ width: 4, height: 22, background: color, borderRadius: 2, boxShadow: isDark ? '0 0 0 2px rgba(0,0,0,0.5)' : '0 0 0 2px #fff', transformOrigin: 'center' }}
                />
            </Box>
        </Box>
    );
}

// A single reference point: a colour-coded tick dropping from the rail to a
// labelled value. The tick lengthens with the row so a staggered label is
// still visibly connected to its point on the rail.
function RefMarker({ p, label, value, color, row, dashed, isDark, delay = 0, tip, onShow, onHide }) {
    const tickH = 16 + row * ROW_STEP;
    const interactive = Boolean(tip);
    return (
        // x position on the plain wrapper, not the motion.div - see YouMarker.
        <Box sx={{ position: 'absolute', left: `${p}%`, top: '50%', transform: 'translateX(-50%)', zIndex: 10 - row }}>
            <Box
                component={motion.div}
                {...(interactive && {
                    tabIndex: 0,
                    role: 'button',
                    'aria-label': `${label}: ${value}`,
                    onMouseEnter: onShow,
                    onMouseLeave: onHide,
                    onFocus: onShow,
                    onBlur: onHide,
                })}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={interactive ? { y: -1 } : undefined}
                transition={{ duration: 0.3, delay }}
                sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    cursor: interactive ? 'help' : 'default',
                    '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2, borderRadius: 2 },
                }}
            >
                <Box sx={{ width: 0, height: `${tickH}px`, borderLeft: dashed ? `2px dashed ${color}` : `2px solid ${color}`, boxShadow: isDark ? '0 0 0 2px rgba(0,0,0,0.45)' : '0 0 0 2px #fff' }} />
                {/* Single-line label (dot · name · value): the two-line stacked block was ~26px
                    tall vs the 21px ROW_STEP, so when all three reference points cluster (platform/
                    industry/top-25% within a couple of points) staggered rows overlapped each other.
                    One line is ~12px, comfortably inside the row step. A solid backdrop stops the
                    You guide line / longer ticks striking through the text. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap', mt: '2px', px: 0.5, borderRadius: 1, bgcolor: 'background.paper' }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.disabled', lineHeight: 1 }}>{label}</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>{value}</Typography>
                </Box>
            </Box>
        </Box>
    );
}

